import { Injectable, computed, inject, signal } from '@angular/core';

import { environment } from '../../../environments/environment';
import { AppSwitcherItem, Apps } from '../models/app-switcher.state';
import { CompanyApiService } from './company-api.service';
import { CurrentUserService } from './current-user.service';
import { UserRolePermissionService } from './user-role-permission.service';

/** Maps each AppId to its display config. URLs and logos come from environment/assets. */
function buildAppCatalog(): Record<number, AppSwitcherItem> {
  return {
    [Apps.TPMPortal]: { id: Apps.TPMPortal, label: 'TPM', logo: 'assets/tpmlogo.svg', url: environment.tpmUrl },
    [Apps.POS]: { id: Apps.POS, label: 'POS', logo: 'assets/poslogo.svg', url: environment.posUrl },
    [Apps.CompanyPortal]: {
      id: Apps.CompanyPortal,
      label: 'Company',
      logo: 'assets/brijjlogo.svg',
      url: environment.companyUrl
    },
    [Apps.Inventory]: { id: Apps.Inventory, label: 'Inventory', logo: 'assets/imslogo.svg', url: environment.imsUrl },
    [Apps.Lister]: { id: Apps.Lister, label: 'Lister', logo: 'assets/listerlogo.svg', url: environment.listerUrl }
  };
}

@Injectable({ providedIn: 'root' })
export class AppSwitcherService {
  private readonly companyApi = inject(CompanyApiService);
  private readonly currentUser = inject(CurrentUserService);
  private readonly userRolePermissionService = inject(UserRolePermissionService);

  private readonly _apps = signal<AppSwitcherItem[]>([]);
  private readonly _loaded = signal(false);
  private readonly _loading = signal(false);
  private readonly _storeName = signal('');
  private readonly _selectedStoreName = signal('');
  private readonly _defaultStoreId = signal<number | null>(null);
  /** Display name (firstName + lastName) from Company API /Users/{id}. Not available in JWT. */
  private readonly _displayName = signal('');

  /** Apps available to the current user (excludes Donation since we're already in it). */
  readonly apps = this._apps.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly hasApps = computed(() => this._apps().length > 0);

  /** Store/location name from Company API (user profile default). */
  readonly storeName = this._storeName.asReadonly();

  /** Name of the currently active/selected store (may differ from profile default if user switched stores). */
  readonly selectedStoreName = this._selectedStoreName.asReadonly();

  /** Default store ID for the current user from Company API. */
  readonly defaultStoreId = this._defaultStoreId.asReadonly();

  /** Full display name (firstName + lastName) from Company API — not in JWT. */
  readonly displayName = this._displayName.asReadonly();

  /** Called by AppLayoutComponent once the active store is resolved from the stores list. */
  setSelectedStoreName(name: string): void {
    this._selectedStoreName.set(name);
  }

  /** Fetch user apps from Company API. Call once after login/auth is confirmed. */
  loadApps(): void {
    if (this._loaded() || this._loading()) return;

    const userId = Number(this.currentUser.userId());
    if (!userId) return;

    this._loading.set(true);

    this.companyApi.getUserDetails(userId).subscribe({
      next: res => {
        this._storeName.set(res.storeName || res.locationName || '');
        this._defaultStoreId.set(res.defaultStoreId ?? null);

        // Populate displayName from User API — not available in JWT
        const displayName = res.displayName?.trim() ?? '';
        this._displayName.set(displayName);
        this.currentUser.setApiDisplayName(displayName);

        // Fetch granular roles and permissions from the dedicated endpoint
        this.userRolePermissionService.fetchAndSetRoles(userId).subscribe(() => {
          // Permissions are now resolved — apply default store based on permission + prior selection
          const canSwitchStore = this.userRolePermissionService.hasPermission('StoreContext:Switch');
          if (res.defaultStoreId) {
            if (!canSwitchStore) {
              // No permission: always use the API default, ignoring any stored value
              this.currentUser.setStoreId(res.defaultStoreId);
            } else if (!this.currentUser.hadStoredStoreId) {
              // Has permission but no prior explicit selection this session: use API default
              this.currentUser.setStoreId(res.defaultStoreId);
            }
            // else: has permission + had an explicit selection — keep their stored choice
          }
          const ids = (res.apps ?? []).map(a => a.id);
          this.resolveApps(ids);
        });
      },
      error: () => {
        // Silently fail — switcher won't show apps
        this._loading.set(false);
        this._loaded.set(true);
      }
    });
  }

  private resolveApps(appIds: number[]): void {
    const catalog = buildAppCatalog();
    // Exclude Donation (current app) from the list
    const items = appIds.filter(id => id !== Apps.Donation && catalog[id]).map(id => catalog[id]);
    this._apps.set(items);
    this._loading.set(false);
    this._loaded.set(true);
  }
}
